/**
 * Entity escalate actions for the Work Queue inbox (CT-5094).
 *
 * Escalate is NOT reassign. Reassign (CT-5093, POST /v1/task/reassign/{task_uuid})
 * moves an EXISTING task by its task uuid. Escalate is ENTITY-keyed: given a
 * truth / claim / change_request, it CREATES an open task for the assignee (or
 * reassigns the existing one) — the response says which via `action`. It also
 * carries reason / severity / message, matching the Escalate modal fields.
 *
 * Mirrors the beta Governance Studio `src/services/escalations.js` — same GS
 * backend, same envelope. entityType (contract: truth|claim|change_request):
 *   change_request → POST /v1/truth/change-request/escalate/{entityUuid}
 *   claim          → POST /v1/workspace/claim/escalate/{entityUuid}
 *   truth          → POST /v1/truth/escalate/{entityUuid}
 *
 * Body: { assigneeUuid (required), severity?, reason?, message? }.
 * Success body: { taskUuid, assigneeUuid, action: 'created'|'reassigned', status, updatedAt }.
 *
 * ERROR CONTRACT: same GS envelope as task actions — the real outcome lives in
 * `statusCode` / `msgCode`, not the HTTP status line. We run the envelope through
 * `extractApiError` and throw an Error carrying `.status` (internal statusCode) +
 * `.code` (msgCode). A bare HTTP error (no envelope) is caught defensively and
 * normalized to the same Error shape.
 */

import { extractApiError, validateApiResponse } from '../shared/utils/apiResponse.js';
import { gsApi } from './apiClient.js';

/** entityType → escalate path builder. Keys match the contract's lowercase types. */
const ESCALATE_PATH = {
  change_request: (uuid) => `/v1/truth/change-request/escalate/${uuid}`,
  claim: (uuid) => `/v1/workspace/claim/escalate/${uuid}`,
  truth: (uuid) => `/v1/truth/escalate/${uuid}`,
};

/** Build a normalized escalate Error carrying the internal status + msgCode. */
function escalationError(message, status, code, fallbackMessage) {
  const error = new Error(message || fallbackMessage);
  if (status != null) error.status = status;
  if (code) error.code = code;
  return error;
}

/** First msgCode string from a GS envelope, or null. */
function firstMsgCode(envelope) {
  const codes = envelope?.msgCode ?? envelope?.msg_code ?? [];
  return codes[0]?.code ?? null;
}

/**
 * Escalate an entity to an assignee — creates (or reassigns) the open task.
 *
 * @param {{ entityType: string, entityUuid: string, assigneeUuid: string,
 *           severity?: string, reason?: string, message?: string }} params
 * @returns {Promise<Object|null>} the unwrapped success body
 *   ({ taskUuid, assigneeUuid, action, status, updatedAt }), or `null` when the
 *   entityType is not escalatable or a required id is missing (no call is made).
 * @throws {Error} on a GS-enveloped error (`.status` + `.code`) or a bare HTTP
 *   error (`.status` only).
 */
export async function escalateEntity({
  entityType,
  entityUuid,
  assigneeUuid,
  severity,
  reason,
  message,
} = {}) {
  const normalizedType =
    typeof entityType === 'string' ? entityType.trim().toLowerCase() : entityType;
  const buildPath = ESCALATE_PATH[normalizedType];
  if (!buildPath || !entityUuid || !assigneeUuid) return null;

  const payload = { assigneeUuid };
  if (severity) payload.severity = severity;
  if (reason) payload.reason = reason;
  if (message) payload.message = message;

  const fallbackMessage = 'Failed to escalate';
  let envelope;
  try {
    envelope = await gsApi.post(buildPath(entityUuid), payload);
  } catch (rejected) {
    // Defensive fallback: a bare HTTP error with no GS envelope.
    throw escalationError(rejected?.message, rejected?.status, null, fallbackMessage);
  }
  const apiError = extractApiError(envelope, fallbackMessage);
  if (apiError) {
    throw escalationError(apiError.message, apiError.statusCode, firstMsgCode(envelope), fallbackMessage);
  }
  return validateApiResponse(envelope, fallbackMessage).body;
}
